import React, { useRef, useState, useEffect } from 'react';
import './NeedsIdent.css';
import * as XLSX from 'xlsx';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getDistinctFilters } from '../Components/Menu/Branch';
import { trainingNeedsAPI, trngCourseAPI, employeeAPI } from './api';
import FuncAreaViewer from '../Components/Viewers/FuncAreaViewer';
import TrngCourseViewer from '../Components/Viewers/TrngCourseViewer';
import ActionBar from '../Components/BottomActionBar/ActionBar';
import DataTable from './DataTable';
import UpdateComponent from '../Components/UpdateComponent/UpdateComponent';
import { useAuth } from './auth';
import { getEmployeeBySalCode } from './cache';

const MIN_SALARY_SLOTS = 20;
const emailPrefix = (email) => (email ? email.split('@')[0] : '');

function NeedsIdent() {
  const { user } = useAuth();
  const [userEmail, setUserEmail] = useState(user?.emp_email || '');
  const [selected, setSelected] = useState(null);

  // Resolve logged-in user's emp_email via cache (in case session has null emp_email)
  useEffect(() => {
    if (!userEmail && user?.sal_code) {
      getEmployeeBySalCode(user.sal_code)
        .then(emp => { if (emp?.emp_email) setUserEmail(emp.emp_email); })
        .catch(() => {});
    }
  }, [user?.sal_code]);

  // Distinct filter options from API
  const [branches, setBranches] = useState([]);
  const [zones, setZones] = useState([]);
  const [franchises, setFranchises] = useState([]);

  useEffect(() => {
    getDistinctFilters().then(({ branches: b, zones: z, franchises: f }) => {
      setBranches(b);
      setZones(z);
      setFranchises(f);
    });
  }, []);

  // Division / Zone / Branch / Franchise
  const [isDivision, setIsDivision] = useState(false);
  const [division, setDivision] = useState('');
  const [isZone, setIsZone] = useState(false);
  const [zone, setZone] = useState('');
  const [isBranch, setIsBranch] = useState(false);
  const [branchSel, setBranchSel] = useState('');
  const [isFranchise, setIsFranchise] = useState(false);
  const [franchiseSel, setFranchiseSel] = useState('');

  // Functional area & training course
  const [funcArea, setFuncArea] = useState('');
  const [funcAreaCode, setFuncAreaCode] = useState('');
  const [trainingCourse, setTrainingCourse] = useState('');
  const [trngCourseCode, setTrngCourseCode] = useState('');
  const [dateOfNeed, setDateOfNeed] = useState(() => new Date());
  const [dateOfNeedOpen, setDateOfNeedOpen] = useState(false);

  // Viewer modals
  const [showFuncAreaViewer, setShowFuncAreaViewer] = useState(false);
  const [showTrngCourseViewer, setShowTrngCourseViewer] = useState(false);

  // Salary code table
  const [salCodes, setSalCodes] = useState(Array(MIN_SALARY_SLOTS).fill(''));

  // Excel import
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);

  // DataTable / ActionBar state (same pattern as FuncArea.jsx)
  const [tableData, setTableData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [showEditCols, setShowEditCols] = useState(false);
  const [editRows, setEditRows] = useState({});
  const [selectedForUpdate, setSelectedForUpdate] = useState({});
  const [selectedForDelete, setSelectedForDelete] = useState({});
  const [insertMsg, setInsertMsg] = useState(null);
  const [updateMsg, setUpdateMsg] = useState(null);
  const [deleteMsg, setDeleteMsg] = useState(null);
  const [sortBy, setSortBy] = useState('');
  const [activeCourses, setActiveCourses] = useState([]);

  const handleRadio = (value) => {
    setSelected(prev => (prev === value ? null : value));
    setSalCodes(Array(MIN_SALARY_SLOTS).fill(''));
  };

  // --- Viewer handlers ---
  const handleFuncAreaSelect = (row) => {
    setFuncArea(row.func_sub_area_name || '');
    setFuncAreaCode(row.func_sub_area_code || '');
    setShowFuncAreaViewer(false);
  };

  const handleTrngCourseSelect = (row) => {
    setTrainingCourse(row.trng_course_name || '');
    setTrngCourseCode(String(row.trng_course_code || ''));
    setShowTrngCourseViewer(false);
  };

  // --- Salary code table ---
  const handleSalCodeChange = (idx, val) => {
    setSalCodes(prev => { const n = [...prev]; n[idx] = val; return n; });
  };

  // --- Excel upload ---
  const handlePickFile = () => fileInputRef.current?.click();
  const handleFileChange = (e) => { setFile(e.target.files?.[0] || null); setUploadStatus(null); };

  const handleUploadExcel = async () => {
    if (!file) { setUploadStatus({ ok: false, msg: 'Please choose an Excel file first.' }); return; }
    setUploadStatus({ ok: false, msg: 'Parsing...' });
    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array', cellDates: true });
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '', raw: false });
      if (!rows.length) { setUploadStatus({ ok: false, msg: 'No data rows found in sheet.' }); return; }

      const parsed = [];
      const errors = [];
      rows.forEach((row, i) => {
        const sal = row['sal_code'] ?? row['SAL_CODE'] ?? row['Sal_Code'] ?? row['Sal Code'];
        const tcc = row['trng_course_code'] ?? row['TRNG_COURSE_CODE'] ?? row['Trng_Course_Code'] ?? row['Trng Course Code'];
        const sal_code = typeof sal === 'string' ? sal.trim() : sal;
        const trng_course_code = typeof tcc === 'string' ? tcc.trim() : tcc;
        if (!sal_code) { errors.push(i + 2); return; }
        parsed.push({ sal_code: sal_code, trng_course_code: trng_course_code ? Number(trng_course_code) : null });
      });

      const slots = Math.max(MIN_SALARY_SLOTS, parsed.length);
      const filled = Array(slots).fill('');
      parsed.forEach((row, i) => { filled[i] = String(row.sal_code ?? ''); });
      setSalCodes(filled);

      if (!trngCourseCode && parsed[0]?.trng_course_code) {
        const code = String(parsed[0].trng_course_code);
        setTrngCourseCode(code);
        try {
          const tc = await trngCourseAPI.getAll({ trng_course_code: code });
          setTrainingCourse(Array.isArray(tc) && tc[0] ? tc[0].trng_course_name : code);
        } catch { setTrainingCourse(code); }
      }

      setUploadStatus({ ok: true, msg: `Parsed ${parsed.length} record(s).${errors.length ? ` ${errors.length} row(s) skipped.` : ''} Click Insert to save.` });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e) {
      setUploadStatus({ ok: false, msg: 'Failed to parse Excel file.' });
    }
  };

  // --- ActionBar handlers (same pattern as FuncArea.jsx) ---
  const clearMessages = () => { setInsertMsg(null); setUpdateMsg(null); setDeleteMsg(null); };

  const handleInsert = async () => {
    clearMessages();
    setShowTable(false);
    setTableData([]);
    setShowEditCols(false);
    if (!trngCourseCode || !trngCourseCode.trim()) { alert('Please select a Training Course.'); return; }
    if (!dateOfNeed) { alert('Please select a Date of Need.'); return; }

    const today = new Date().toLocaleDateString('en-CA');
    const identifiedBy = user?.sal_code != null ? String(user.sal_code) : null;
    const capturedCourseCode = trngCourseCode; // capture before any state changes
    console.log('[NeedsIdent] handleInsert — user object:', user);
    console.log('[NeedsIdent] handleInsert — identifiedBy:', identifiedBy, '| user.emp_email:', user?.emp_email, '| capturedCourseCode:', capturedCourseCode);
    let codes = [];

    if (selected === 'option3') {
      const filterParams = {};
      if (isDivision && division) filterParams.division = division;
      if (isZone && zone) filterParams.zone = zone;
      if (isBranch && branchSel) filterParams.branch = branchSel;
      if (isFranchise && franchiseSel) filterParams.franchise = franchiseSel;
      try {
        const employees = await employeeAPI.getByFilters(filterParams);
        codes = (Array.isArray(employees) ? employees : []).map(e => String(e.sal_code ?? '')).filter(Boolean);
      } catch (err) {
        alert('Failed to fetch employees. Please try again.');
        return;
      }
      if (!codes.length) { alert('No employees found for the selected filters.'); return; }
      if (!window.confirm(`Do you want to insert ${codes.length} Employee(s)?`)) return;
    } else {
      codes = salCodes.filter(s => s.trim());
      if (!codes.length) { alert('Please enter at least one Salary Code.'); return; }
    }

    let ok = 0;
    const insertedRows = [];
    const failMsgs = [];
    for (const sc of codes) {
      const sal_code = sc.trim();
      const payload = {
        sal_code,
        trng_course_code: Number(trngCourseCode),
        date_of_entry: today,
        date_of_need: dateOfNeed.toLocaleDateString('en-CA'),
        ...(identifiedBy ? { identified_by: identifiedBy } : {}),
        ...(isDivision && division ? { division } : {}),
      };
      try {
        console.log('[NeedsIdent] create payload:', payload);
        const res = await trainingNeedsAPI.create(payload);
        console.log('[NeedsIdent] create response status:', res.status, 'ok:', res.ok);
        if (res.ok) {
          ok++;
          const saved = await res.json();
          console.log('[NeedsIdent] create saved item:', saved);
          const item = saved.item || {};
          insertedRows.push({
            sal_code: item.sal_code ?? sal_code,
            emp_name: '',
            func_area: funcArea,
            trng_course_name: trainingCourse,
            trng_course_code: item.trng_course_code ?? Number(trngCourseCode),
            date_of_entry: item.date_of_entry ?? today,
            date_of_need: item.date_of_need ?? dateOfNeed.toLocaleDateString('en-CA'),
            nomin_batch_sr_no: item.nomin_batch_sr_no ?? 0,
            nomin_confirm: item.nomin_confirm ?? 'N',
            identified_by: item.identified_by ?? identifiedBy ?? '',
            identified_by_email: emailPrefix(userEmail) || identifiedBy || '',
            updated_by: '',
            updated_by_name: ''
          });
        } else {
          const errBody = await res.json().catch(() => ({}));
          failMsgs.push(errBody.message || `${sal_code}: insert failed`);
        }
      } catch (err) { failMsgs.push(`${sal_code}: insert failed`); }
    }

    if (ok > 0 && selected !== 'option3') setSalCodes(Array(MIN_SALARY_SLOTS).fill(''));

    // Fetch employee names for inserted rows
    if (insertedRows.length) {
      try {
        const empData = await employeeAPI.getBySalCodes(insertedRows.map(r => r.sal_code));
        const nameMap = {};
        (Array.isArray(empData) ? empData : []).forEach(e => { nameMap[String(e.sal_code)] = e.emp_name || ''; });
        insertedRows.forEach(r => { r.emp_name = nameMap[String(r.sal_code)] || ''; });
      } catch { /* skip, names stay empty */ }
    }

    setTableData(insertedRows);
    setShowTable(insertedRows.length > 0);
    setShowEditCols(false);
    const failText = failMsgs.length ? ` Errors: ${failMsgs.join('; ')}` : '';
    setInsertMsg({
      text: ok > 0 ? `${ok} record(s) inserted successfully.${failText}` : `Insert failed.${failText}`,
      success: ok > 0
    });
  };

  const handleView = async () => {
    clearMessages();
    setShowEditCols(false);
    setEditRows({});
    setSelectedForUpdate({});
    setSelectedForDelete({});
    console.log('[NeedsIdent] handleView — trngCourseCode:', trngCourseCode);
    const data = await fetchTableData(trngCourseCode);
    console.log('[NeedsIdent] handleView data:', data);
    setTableData(data);
    setShowTable(true);
  };

  const handleUpdate = async () => {
    clearMessages();
    setEditRows({});
    setSelectedForUpdate({});
    setSelectedForDelete({});
    const data = await fetchTableData();
    setTableData(data);
    setShowTable(true);
    setShowEditCols(true);
    // Resolve func_sub_area_code: prefer form state, else derive from first tableData row's trng_course_code
    let fsaForDropdown = funcAreaCode || '';
    if (!fsaForDropdown && data.length > 0 && data[0].trng_course_code) {
      try {
        const courseInfo = await trngCourseAPI.getAll({ trng_course_code: String(data[0].trng_course_code) });
        fsaForDropdown = Array.isArray(courseInfo) && courseInfo[0] ? String(courseInfo[0].func_sub_area_code ?? '') : '';
      } catch (e) { /* skip */ }
    }
    if (fsaForDropdown) {
      try {
        const courses = await trngCourseAPI.getAll({ func_sub_area_code: fsaForDropdown, active_status: true });
        const courseList = Array.isArray(courses) ? courses : [];
        setActiveCourses(courseList);
      } catch (e) { setActiveCourses([]); }
    } else {
      setActiveCourses([]);
    }
  };

  const fetchTableData = async (overrideCourseCode) => {
    const params = {};
    const courseCode = overrideCourseCode || trngCourseCode;
    if (courseCode) params.trainingcoursecode = courseCode;
    else if (funcAreaCode) params.func_sub_area_code = funcAreaCode;
    else if (tableData.length > 0 && tableData[0].trng_course_code) params.trainingcoursecode = String(tableData[0].trng_course_code);
    if (isZone && zone) params.zone = zone;
    if (isBranch && branchSel) params.branch = branchSel;
    console.log('[NeedsIdent] fetchTableData params:', params);
    const data = await trainingNeedsAPI.getTrainingNeeds(params);
    console.log('[NeedsIdent] fetchTableData raw response:', data);
    const rows = Array.isArray(data) ? data : [];

    const mapped = rows.map(r => ({
      ...r,
      identified_by_email: emailPrefix(r.identified_by_email ?? '') || String(r.identified_by ?? ''),
      updated_by_name: emailPrefix(r.updated_by_email ?? '') || String(r.updated_by ?? ''),
    }));
    console.log('[NeedsIdent] fetchTableData mapped rows (identified_by_email):', mapped.map(r => ({ sal_code: r.sal_code, identified_by: r.identified_by, identified_by_email: r.identified_by_email })));
    return mapped;
  };

  const toggleEditRow = (idx, row) => {
    setEditRows(prev => {
      if (prev[idx]) { const n = { ...prev }; delete n[idx]; return n; }
      const rawDate = row.date_of_need ? row.date_of_need.split('T')[0] : '';
      return { ...prev, [idx]: { trng_course_code: String(row.trng_course_code ?? ''), trng_course_name: row.trng_course_name ?? '', date_of_need: rawDate } };
    });
    setSelectedForUpdate(prev => {
      if (prev[idx]) { const n = { ...prev }; delete n[idx]; return n; }
      return { ...prev, [idx]: true };
    });
  };

  const toggleDeleteRow = (idx) => {
    setSelectedForDelete(prev => {
      if (prev[idx]) { const n = { ...prev }; delete n[idx]; return n; }
      return { ...prev, [idx]: true };
    });
  };

  const handleUpdateSelected = async () => {
    const idxs = Object.keys(selectedForUpdate).filter(i => selectedForUpdate[i]);
    if (!idxs.length) { setUpdateMsg({ text: 'No records selected for update.', success: false }); return; }
    let ok = 0, fail = 0;
    for (const idx of idxs) {
      const row = tableData[idx];
      const edited = editRows[idx];
      if (!edited) { continue; }

      const dateOfEntry = row.date_of_entry?.split('T')[0] ?? '';
      const courseChanged = edited.trng_course_code && String(edited.trng_course_code) !== String(row.trng_course_code);
      const dateChanged = edited.date_of_need && edited.date_of_need !== (row.date_of_need?.split('T')[0] ?? '');

      if (!courseChanged && !dateChanged) { continue; }

      const payload = {
        nomin_confirm: row.nomin_confirm ?? 'N',
        updated_by: user?.sal_code != null ? String(user.sal_code) : null,
        ...(courseChanged ? { trng_course_code: Number(edited.trng_course_code) } : {}),
        ...(dateChanged ? { date_of_need: edited.date_of_need } : {}),
      };

      try {
        const res = await trainingNeedsAPI.update(row.sal_code, row.trng_course_code, dateOfEntry, payload);
        if (res.ok) { ok++; } else { fail++; }
      } catch (err) { fail++; }
    }
    if (ok === 0 && fail === 0) {
      setUpdateMsg({ text: 'No changes detected. Nothing to update.', success: false });
    } else {
      setUpdateMsg(
        ok > 0 && fail === 0 ? { text: `${ok} record(s) updated successfully.`, success: true }
        : ok > 0 ? { text: `${ok} updated, ${fail} failed.`, success: false }
        : { text: `Update failed for all ${fail} record(s).`, success: false }
      );
    }
    setSelectedForUpdate({}); setEditRows({});
    const data = await fetchTableData();
    setTableData(data);
  };

  const handleDeleteSelected = async () => {
    const idxs = Object.keys(selectedForDelete).filter(i => selectedForDelete[i]);
    if (!idxs.length) { setDeleteMsg({ text: 'No records selected for delete.', success: false }); return; }
    let ok = 0, fail = 0;
    for (const idx of idxs) {
      const row = tableData[idx];
      try {
        const resp = await trainingNeedsAPI.deleteTrainingNeed(row.sal_code, row.trng_course_code, row.date_of_need || row.date_of_entry);
        if (resp.ok) ok++; else fail++;
      } catch { fail++; }
    }
    if (ok) setDeleteMsg({ text: `${ok} record(s) deleted.`, success: true });
    if (fail) setDeleteMsg({ text: `${fail} record(s) failed.`, success: false });
    setSelectedForDelete({});
    const data = await fetchTableData();
    setTableData(data);
  };

  const handleReset = () => {
    setFuncArea(''); setFuncAreaCode(''); setTrainingCourse(''); setTrngCourseCode('');
    setDateOfNeed(new Date());
    setSalCodes(Array(MIN_SALARY_SLOTS).fill(''));
    setIsDivision(false); setDivision(''); setIsZone(false); setZone('');
    setIsBranch(false); setBranchSel(''); setIsFranchise(false); setFranchiseSel('');
    setTableData([]); setShowTable(false); setShowEditCols(false);
    setEditRows({}); setSelectedForUpdate({}); setSelectedForDelete({});
    clearMessages(); setSortBy('');
    setActiveCourses([]);
    setFile(null); setUploadStatus(null);
    setDateOfNeedOpen(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSortChange = (opt) => {
    setSortBy(opt);
    if (tableData.length) {
      const sorted = [...tableData].sort((a, b) => {
        if (opt === 'Salary Code') return String(a.sal_code || '').localeCompare(String(b.sal_code || ''));
        if (opt === 'Employee Name') return String(a.emp_name || '').localeCompare(String(b.emp_name || ''));
        return 0;
      });
      setTableData(sorted);
    }
  };

  // --- Render helpers ---
  const salRows = [];
  for (let i = 0; i < salCodes.length; i += 2) {
    salRows.push({ left: i, right: i + 1 });
  }

  const modalStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  };
  const modalInner = {
    backgroundColor: 'white', padding: '20px', borderRadius: '8px',
    maxWidth: '90%', maxHeight: '90%', overflow: 'auto'
  };

  return (
    <div className="Body" onClick={clearMessages}>
      {/* FuncArea Viewer Modal */}
      {showFuncAreaViewer && (
        <div style={modalStyle}>
          <div style={modalInner}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3>Select Functional Area</h3>
              <button onClick={() => setShowFuncAreaViewer(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>×</button>
            </div>
            <FuncAreaViewer selectable onSelect={handleFuncAreaSelect} autoLoad />
          </div>
        </div>
      )}

      {/* TrngCourse Viewer Modal */}
      {showTrngCourseViewer && (
        <div style={modalStyle}>
          <div style={modalInner}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3>Select Training Course</h3>
              <button onClick={() => setShowTrngCourseViewer(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>×</button>
            </div>
            <TrngCourseViewer selectable onSelect={handleTrngCourseSelect} funcSubAreaCode={funcAreaCode} autoLoad />
          </div>
        </div>
      )}

      {/* Radio */}
      <div className="formCode">
        <input className="radioInput" type="radio" id="opt2" checked={selected === 'option2'} onChange={() => handleRadio('option2')} />
        <label htmlFor="opt2" className="label">Individual Employees</label>
        <input className="radioInput" type="radio" id="opt3" checked={selected === 'option3'} onChange={() => handleRadio('option3')} style={{ marginLeft: 20 }} />
        <label htmlFor="opt3" className="label">Filter Wise Employees</label>
      </div>

      {(selected === 'option2' || selected === 'option3') && (
        <>
          {/* Division / Zone / Branch / Franchise — only for Filter Wise */}
          {selected === 'option3' && (<>
          {/* Division */}
          <div className="formCode">
            <label className="label">
              <input type="checkbox" checked={isDivision} onChange={(e) => setIsDivision(e.target.checked)} />
              <span style={{ marginLeft: 6 }}>Employees of a Division</span>
            </label>
            <label className="label">Division Name</label>
            <select className="selectInput" disabled={!isDivision} value={division} onChange={(e) => setDivision(e.target.value)}>
              <option value="">--None--</option>
              <option value="Appliances">Appliances</option>
            </select>
          </div>

          {/* Zone */}
          <div className="formCode">
            <label className="label">
              <input type="checkbox" checked={isZone} onChange={(e) => setIsZone(e.target.checked)} />
              <span style={{ marginLeft: 6 }}>Employees of a Zone</span>
            </label>
            <label className="label">Zone Name</label>
            <select className="selectInput" disabled={!isZone} value={zone} onChange={(e) => setZone(e.target.value)}>
              <option value="">--None--</option>
              {zones.map((z, idx) => (<option key={idx} value={z}>{z}</option>))}
            </select>
          </div>

          {/* Branch */}
          <div className="formCode">
            <label className="label">
              <input type="checkbox" checked={isBranch} onChange={(e) => setIsBranch(e.target.checked)} />
              <span style={{ marginLeft: 6 }}>Employees of a Branch</span>
            </label>
            <label className="label">Branch Name</label>
            <select className="selectInput wide150" disabled={!isBranch} value={branchSel} onChange={(e) => setBranchSel(e.target.value)}>
              <option value="">--None--</option>
              {branches.map((b, idx) => (<option key={idx} value={b}>{b}</option>))}
            </select>
          </div>

          {/* Franchise */}
          <div className="formCode">
            <label className="label">
              <input type="checkbox" checked={isFranchise} onChange={(e) => setIsFranchise(e.target.checked)} />
              <span style={{ marginLeft: 6 }}>Employees of a Franchisee</span>
            </label>
            <label className="label">Franchisee Name</label>
            <select className="selectInput wide270" disabled={!isFranchise} value={franchiseSel} onChange={(e) => setFranchiseSel(e.target.value)}>
              <option value="">--None--</option>
              {franchises.map((f, idx) => (<option key={idx} value={f}>{f}</option>))}
            </select>
          </div>

          </>)}

          {/* Functional Area with Viewer */}
          <div className="formCode">
            <label className="label">Functional Area</label>
            <input className="textInput nameInput" type="text" value={funcArea} readOnly />
            <button type="button" className="button" onClick={() => setShowFuncAreaViewer(true)}>Get</button>
            <button type="button" className="button ni-clear-btn" onClick={() => { setFuncArea(''); setFuncAreaCode(''); setTrainingCourse(''); setTrngCourseCode(''); }}>Clear</button>
          </div>

          {/* Training Course with Viewer */}
          <div className="formCode">
            <label className="label">Training Course</label>
            <input className="textInput nameInput" type="text" value={trainingCourse} readOnly />
            <button
              type="button"
              className="button"
              onClick={() => {
                if (!funcAreaCode) { alert('Please select a Functional Area first.'); return; }
                setShowTrngCourseViewer(true);
              }}
            >Get</button>
            <button type="button" className="button ni-clear-btn" onClick={() => { setTrainingCourse(''); setTrngCourseCode(''); }}>Clear</button>
          </div>

          {/* Date of Need */}
          <div className="formCode">
            <label className="label">Date of Need</label>
            <div className="ni-date-wrap" onClick={() => setDateOfNeedOpen(true)}>
              <DatePicker
                open={dateOfNeedOpen}
                onClickOutside={() => setDateOfNeedOpen(false)}
                selected={dateOfNeed}
                onChange={(date) => { setDateOfNeed(date); setDateOfNeedOpen(false); }}
                dateFormat="dd/MM/yyyy"
                className="ni-date-input"
                placeholderText="Select date of needs"
                showIcon
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={100}
                customInput={<input readOnly />}
              />
            </div>
          </div>

        </>
      )}

      {/* Excel Upload Section — only for Individual Employees */}
      {selected === 'option2' && <div className="hrDivider" />}
      {selected === 'option2' && <div className="formCode">
        <label className="label">
          Upload Training Needs (Excel)<span className="important">*</span>
        </label>
        <div className="uploadGroup">
          <div className="fileMeta">
            <span className="hint">Expected headers: <b>sal_code</b>, <b>trng_course_code</b></span>
            <div className="uploadControls">
              <button type="button" className="button" onClick={handlePickFile}>Choose File</button>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" hidden onChange={handleFileChange} />
              <span className="fileName">{file ? file.name : 'No file chosen'}</span>
            </div>
          </div>
          <div className="uploadParams">
            <button type="button" className="button" onClick={handleUploadExcel}>Upload</button>
          </div>
        </div>
      </div>}

      {selected === 'option2' && uploadStatus && (
        <p className={`remarks ${uploadStatus.ok ? 'ok' : 'err'}`}>{uploadStatus.msg}</p>
      )}

      {/* Salary Code Table — only for Individual Employees */}
      {selected === 'option2' && (
        <div className="salaryTableContainer">
          <table className="dataTable salaryTable">
            <thead>
              <tr>
                <th>Sr. No.</th>
                <th>Salary Code / Technician Code</th>
                <th className="salDivider"></th>
                <th>Sr. No.</th>
                <th>Salary Code / Technician Code</th>
              </tr>
            </thead>
            <tbody>
              {salRows.map(({ left, right }) => (
                <tr key={left}>
                  <td style={{ textAlign: 'center' }}>{left < salCodes.length ? left + 1 : ''}</td>
                  <td>
                    {left < salCodes.length && (
                      <input
                        className="dt-edit-input"
                        type="text"
                        value={salCodes[left]}
                        onChange={(e) => handleSalCodeChange(left, e.target.value)}
                      />
                    )}
                  </td>
                  <td className="salDivider"></td>
                  <td style={{ textAlign: 'center' }}>{right < salCodes.length ? right + 1 : ''}</td>
                  <td>
                    {right < salCodes.length && (
                      <input
                        className="dt-edit-input"
                        type="text"
                        value={salCodes[right]}
                        onChange={(e) => handleSalCodeChange(right, e.target.value)}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ActionBar */}
      <ActionBar
        sortOptions={['Salary Code', 'Employee Name']}
        onView={handleView}
        onReset={handleReset}
        onInsert={handleInsert}
        onUpdate={handleUpdate}
        onSortChange={handleSortChange}
        selectedSort={sortBy}
      />

      {insertMsg && (
        <p style={{ textAlign: 'center', fontSize: '11px', fontFamily: 'GEG-Body, sans-serif', color: insertMsg.success ? 'green' : 'red', margin: '4px 0' }}>
          {insertMsg.text}
        </p>
      )}
      {updateMsg && (
        <p style={{ textAlign: 'center', fontSize: '11px', fontFamily: 'GEG-Body, sans-serif', color: updateMsg.success ? 'green' : 'red', margin: '4px 0' }}>
          {updateMsg.text}
        </p>
      )}
      {deleteMsg && (
        <p style={{ textAlign: 'center', fontSize: '11px', fontFamily: 'GEG-Body, sans-serif', color: deleteMsg.success ? 'green' : 'red', margin: '4px 0' }}>
          {deleteMsg.text}
        </p>
      )}

      {showTable && (
        <DataTable
          columns={[
            { header: 'Sr No', key: 'sr_no', renderCell: (_v, _r, idx) => idx + 1 },
            { header: 'Employee Name', key: 'emp_name', accessor: (r) => r.emp_name ?? ''},
            { header: 'SalCode', key: 'sal_code', accessor: (r) => r.sal_code ?? '' },
            { header: 'Functional Area', key: 'func_area', accessor: (r) => r.func_area ?? r.func_sub_area_name ?? funcArea ?? '' },
            { header: 'Date of Identification (DD/MM/YY)', key: 'date_of_need', editable: true, inputProps: { type: 'date' }, accessor: (r) => { if (!r.date_of_need) return ''; const [y,m,d] = r.date_of_need.split('T')[0].split('-'); return `${d}/${m}/${y.slice(-2)}`; } },
            {
              header: 'Training Course', key: 'trng_course_name',
              editable: true,
              accessor: (r) => r.trng_course_name ?? '',
              renderEdit: (idx, eRows, onChange) => {
                return (
                  <select
                    className="dt-edit-input"
                    value={eRows[idx]?.trng_course_code ?? ''}
                    onChange={(e) => {
                      const sel = activeCourses.find(c => String(c.trng_course_code) === e.target.value);
                      onChange(idx, 'trng_course_code', e.target.value);
                      onChange(idx, 'trng_course_name', sel?.trng_course_name ?? '');
                    }}
                  >
                    <option value="">--Select--</option>
                    {activeCourses.map(c => (
                      <option key={c.trng_course_code} value={String(c.trng_course_code)}>{c.trng_course_name}</option>
                    ))}
                  </select>
                );
              }
            },
            { header: 'Nominated Batch', key: 'nomin_batch_sr_no', accessor: (r) => r.nomin_batch_sr_no ?? 0 },
            { header: 'Confirmed', key: 'nomin_confirm', accessor: (r) => r.nomin_confirm ?? 'N' },
            { header: 'Attended', key: 'attended_yn', accessor: (r) => r.attended_yn ?? 'N' },
            { header: 'Identified By', key: 'identified_by_email', accessor: (r) => r.identified_by_email ?? '' },
            { header: 'Updated By', key: 'updated_by_name', accessor: (r) => r.updated_by_name ?? '' },
          ]}
          data={tableData}
          showEditCols={showEditCols}
          editRows={editRows}
          onToggleEditRow={toggleEditRow}
          onChangeEditCell={(idx, key, value) => setEditRows(prev => ({ ...prev, [idx]: { ...prev[idx], [key]: value } }))}
          selectedForDelete={selectedForDelete}
          onToggleDeleteRow={toggleDeleteRow}
          onSelectAllDelete={showEditCols ? (checked) => {
            if (checked) {
              const all = {};
              tableData.forEach((_, i) => { all[i] = true; });
              setSelectedForDelete(all);
            } else {
              setSelectedForDelete({});
            }
          } : null}
          emptyMessage="No data found"
          rowKey={(row, idx) => `${row.sal_code}-${row.trng_course_code}-${idx}`}
        />
      )}
      {showTable && showEditCols && (
        <UpdateComponent onUpdateSelected={handleUpdateSelected} onDeleteSelected={handleDeleteSelected} />
      )}
    </div>
  );
}

export default NeedsIdent;
